import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, CLIENT_PRIVILEGED } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_READ_QUESTIONNAIRES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST, CLIENT_PRIVILEGED];
const CAN_MANAGE_QUESTIONNAIRES = [GLOBAL_ADMIN, LOCATION_ADMIN];

const CAN_READ_QUESTIONNAIRES_GLOBAL = [GLOBAL_ADMIN, CLIENT_PRIVILEGED];
const CAN_MANAGE_QUESTIONNAIRES_GLOBAL = [GLOBAL_ADMIN];

export default class QuestionnaireAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;

  get canRead(): boolean {
    return this.#allowedAtLocation(Permission.VISITORS_QUESTIONNAIRE_READ, CAN_READ_QUESTIONNAIRES);
  }

  get canReadGlobal(): boolean {
    return this.#allowedAtCompany(Permission.VISITORS_QUESTIONNAIRE_READ, CAN_READ_QUESTIONNAIRES_GLOBAL);
  }

  get canCreate(): boolean {
    return this.#allowedAtLocation(Permission.VISITORS_QUESTIONNAIRE_CREATE, CAN_MANAGE_QUESTIONNAIRES);
  }

  get canCreateGlobal(): boolean {
    return this.#allowedAtCompany(Permission.VISITORS_QUESTIONNAIRE_CREATE, CAN_MANAGE_QUESTIONNAIRES_GLOBAL);
  }

  get canUpdate(): boolean {
    return this.#allowedAtLocation(Permission.VISITORS_QUESTIONNAIRE_UPDATE, CAN_MANAGE_QUESTIONNAIRES);
  }

  get canUpdateGlobal(): boolean {
    return this.#allowedAtCompany(Permission.VISITORS_QUESTIONNAIRE_UPDATE, CAN_MANAGE_QUESTIONNAIRES_GLOBAL);
  }

  get canDelete(): boolean {
    return this.#allowedAtLocation(Permission.VISITORS_QUESTIONNAIRE_DELETE, CAN_MANAGE_QUESTIONNAIRES);
  }

  get canDeleteGlobal(): boolean {
    return this.#allowedAtCompany(Permission.VISITORS_QUESTIONNAIRE_DELETE, CAN_MANAGE_QUESTIONNAIRES_GLOBAL);
  }

  get canNavigate(): boolean {
    return this.#allowedAtLocation(Permission.VISITORS_QUESTIONNAIRE_NAV, CAN_READ_QUESTIONNAIRES);
  }

  get canNavigateToGlobal(): boolean {
    return this.#allowedAtCompany(Permission.VISITORS_QUESTIONNAIRE_NAV, CAN_READ_QUESTIONNAIRES_GLOBAL);
  }

  #allowedAtLocation(permission: Permission, roles: string[]): boolean {
    if (!this.state.features?.canAccessQuestionnaires) return false;
    if (this.featureFlags.isEnabled('questionnaire-policy-use-authz')) {
      return this.authz.hasPermissionAtCurrentLocation(permission);
    }
    return _intersection(roles, this.currentAdmin.roleNames).length > 0;
  }

  // `roleNames` is scoped to the current company/location via
  // `CurrentAdminService#setRolesTask` — `companyRoles` is NOT (it returns the
  // full cross-company list and would leak Global Admin / Client Privileged
  // grants from sibling companies into the active context).
  #allowedAtCompany(permission: Permission, roles: string[]): boolean {
    if (!this.state.features?.canAccessQuestionnaires) return false;
    if (this.featureFlags.isEnabled('questionnaire-policy-use-authz')) {
      return this.authz.hasPermissionAtCurrentCompany(permission);
    }
    return _intersection(roles, this.currentAdmin.roleNames).length > 0;
  }
}
